import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import SuperadminDashboard from "@/pages/SuperadminDashboard";
import InstructorDashboard from "@/pages/instructor/InstructorDashboard";
import SimulationLobby from "@/pages/student/SimulationLobby";
import HospitalDashboard from "@/pages/student/HospitalDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Send cookies with every request
axios.defaults.withCredentials = true;

// Initialize theme before render to prevent flash
(() => {
  const theme = localStorage.getItem("medboard_theme");
  if (theme === "dark" || (!theme && window.matchMedia("(prefers-color-scheme: dark)").matches)) {
    document.documentElement.classList.add("dark");
  }
})();

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, role, invite_code) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password, role, invite_code });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    setUser(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-emerald-600 rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === "superadmin") return <Navigate to="/admin" replace />;
    return <Navigate to={user.role === "instructor" ? "/instructor" : "/simulations"} replace />;
  }
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />

          <Route path="/simulations" element={
            <ProtectedRoute allowedRoles={["student"]}><SimulationLobby /></ProtectedRoute>
          } />
          <Route path="/simulation/:simId" element={
            <ProtectedRoute allowedRoles={["student"]}><HospitalDashboard /></ProtectedRoute>
          } />

          <Route path="/view/hospital/:hospitalId" element={
            <ProtectedRoute allowedRoles={["instructor", "superadmin"]}><HospitalDashboard /></ProtectedRoute>
          } />

          <Route path="/instructor" element={
            <ProtectedRoute allowedRoles={["instructor", "superadmin"]}><InstructorDashboard /></ProtectedRoute>
          } />
          <Route path="/instructor/simulation/:simId" element={
            <ProtectedRoute allowedRoles={["instructor", "superadmin"]}><InstructorDashboard /></ProtectedRoute>
          } />

          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={["superadmin"]}><SuperadminDashboard /></ProtectedRoute>
          } />

          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="w-12 h-12 border-4 border-gray-200 border-t-emerald-600 rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "superadmin") return <Navigate to="/admin" replace />;
  return <Navigate to={user.role === "instructor" ? "/instructor" : "/simulations"} replace />;
};

export default App;
